import { useEffect, useRef, useState } from 'react';
import useDebouncedValue from './useDebouncedValue';
import type { DuplicateMatch } from '../types/duplicateMatch';

export interface UseDuplicateCheckResult {
  /** The current duplicate matches (empty while the query is not meaningful or the lookup found none). */
  matches: DuplicateMatch[];
  /** True once the user has clicked "Add anyway" for the current (debounced) query. */
  dismissed: boolean;
  /** Hide the nudge for the current query; it reappears once the query changes again. */
  dismiss: () => void;
}

/**
 * Create-time "did you mean?" duplicate lookup shared by the Farmer / Farm / Plot create dialogs
 * (Plan C, Layer 1). Presentational-only, and it NEVER blocks submit.
 *
 * `signature` is a stable string describing the current inputs (e.g. `email|phone|name`). When it is
 * `null` or empty the check is skipped and any stale matches are cleared — this is how the nudge
 * degrades gracefully in inline-create contexts where no meaningful query exists yet (no parent
 * selected, name too short, or the feature switched off). Otherwise the debounced signature is the
 * sole trigger for a fresh `fetcher()` call.
 *
 * Out-of-order responses are dropped with a latest-wins request token (mirrors
 * {@link ../components/FarmWorkspace/dialogs/EntitySearchAutocomplete}); the same token invalidates
 * any in-flight response on unmount so it can't setState afterwards. The `fetcher` is read from a ref
 * so it is never a hook dependency — the debounced signature alone drives the effect, and the ref
 * always holds the latest render's closure (fresh input values) by the time the debounce settles.
 */
export function useDuplicateCheck(
  signature: string | null,
  fetcher: () => Promise<DuplicateMatch[]>,
  delay: number = 300,
): UseDuplicateCheckResult {
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(null);
  const debouncedSignature = useDebouncedValue(signature, delay);
  const requestIdRef = useRef(0);

  // Keep the ref pointing at the latest render's fetcher WITHOUT touching it during render (which the
  // react-hooks/refs rule forbids). This effect runs after every commit, so by the time the debounced
  // signature settles the ref holds the current closure (fresh input values).
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });

  useEffect(() => () => {
    // Invalidate any in-flight response so it can't setState after unmount.
    requestIdRef.current += 1;
  }, []);

  useEffect(() => {
    const requestId = (requestIdRef.current += 1);
    let active = true;
    // All setState happens inside this async callback (never synchronously in the effect body), so
    // the debounced signature clears stale matches and applies fresh ones the same way.
    const run = async () => {
      if (!debouncedSignature) {
        if (active && requestId === requestIdRef.current) setMatches([]);
        return;
      }
      try {
        const found = await fetcherRef.current();
        if (active && requestId === requestIdRef.current) setMatches(found);
      } catch {
        if (active && requestId === requestIdRef.current) setMatches([]);
      }
    };
    void run();
    return () => {
      active = false;
    };
  }, [debouncedSignature]);

  const dismiss = () => setDismissedSignature(debouncedSignature);
  const dismissed = dismissedSignature !== null && dismissedSignature === debouncedSignature;

  return { matches, dismissed, dismiss };
}

export default useDuplicateCheck;
